// LOCAL-DEV ONLY (devstack): make the app boot with no AWS and no mhub monolith.
//
// The bootstrap in contexts/Auth/Provider.jsx gates the whole app on four calls that the local
// devstack cannot answer, because the services behind them have no local stack:
//   1. Auth.currentAuthenticatedUser() / Auth.currentSession()  -> real Cognito
//   2. getAuthentication()  -> cookie-authenticated GET on the mhub monolith (api-dev.mhub.my)
//   3. setCurrentSession()  -> API.post('USER', '/usersession/create') on the user API gateway
//   4. listUsers / listCompanyBranches -> the user AppSync endpoint (mhub-user-api has no local/)
// A forged Cognito token alone defeats none of 2-4, so running offline needs this code shim.
//
// Everything here is behind REACT_APP_LOCAL_AUTH_SHIM=1. When that env is unset (staging/prod
// builds) every export is inert and callers fall through to the real implementation: zero
// behavior change. Mirrors the pattern already used by src/utils/storageShim.js.
//
// The forged identity below is NOT arbitrary — it has to match the rows the backend stacks seed
// (local/seed.sh in each API repo), or their resolvers reject the request:
//   user u-1 / session sess-1  -> the user_sessions_local row that util.AppsyncCheckUserSession
//                                 compares against, read from the request VTL as
//                                 $context.identity.username and claims['origin_jti']
//   company co-1 / branch br-1 -> custom:company_id on every seeded record
//   DEVELOPER_HEADQUARTERS     -> the role-gated VTL reads $context.identity.groups; it is also in
//                                 the FE's ALLOWED_GROUPS + UPPER_GROUPS, so the UI grants access
// The local AppSync simulators do not verify JWT signatures, which is what makes this work — and
// is exactly why it must never be enabled outside local dev.

export const LOCAL_AUTH_SHIM = process.env.REACT_APP_LOCAL_AUTH_SHIM === '1';

export const LOCAL_USER_ID = 'u-1';
export const LOCAL_SESSION_ID = 'sess-1';
export const LOCAL_COMPANY_ID = 'co-1';
export const LOCAL_COMPANY_BRANCH_ID = 'br-1';
export const LOCAL_GROUPS = ['DEVELOPER_HEADQUARTERS'];

const POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || 'ap-southeast-1_irgzTGv6p';
const CLIENT_ID = process.env.REACT_APP_COGNITO_APP_CLIENT_ID || 'local';
const REGION = process.env.REACT_APP_AWS_REGION || 'ap-southeast-1';

const b64url = (obj) => btoa(JSON.stringify(obj))
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/, '');

// Claims are kept byte-for-byte in step with the forged token in each repo's local/test-query.sh,
// so a request from the browser and a request from `make -C local test` are indistinguishable to
// the simulators. exp is far future so the session never expires mid-session.
export const LOCAL_ID_TOKEN_PAYLOAD = {
  sub: LOCAL_USER_ID,
  'cognito:username': LOCAL_USER_ID,
  'cognito:groups': LOCAL_GROUPS,
  origin_jti: LOCAL_SESSION_ID,
  'custom:company_id': LOCAL_COMPANY_ID,
  'custom:company_branch_id': LOCAL_COMPANY_BRANCH_ID,
  email: 'local.dev@example.com',
  token_use: 'id',
  aud: CLIENT_ID,
  exp: 2000000000,
  iat: 1700000000,
  iss: `https://cognito-idp.${REGION}.amazonaws.com/${POOL_ID}`,
};

export const localIdToken = () => [
  b64url({ alg: 'RS256', typ: 'JWT', kid: 'local' }),
  b64url(LOCAL_ID_TOKEN_PAYLOAD),
  'localsig',
].join('.');

// Minimal stand-ins for the amazon-cognito-identity-js shapes the app actually touches:
// session.getIdToken().getJwtToken(), session.idToken.payload.sub, user.getSignInUserSession().
const makeToken = () => {
  const jwt = localIdToken();
  return { getJwtToken: () => jwt, payload: LOCAL_ID_TOKEN_PAYLOAD, jwtToken: jwt };
};

export const localSession = () => {
  const token = makeToken();
  return {
    getIdToken: () => token,
    getAccessToken: () => token,
    getRefreshToken: () => ({ getToken: () => 'local-refresh' }),
    isValid: () => true,
    idToken: token,
    accessToken: token,
  };
};

export const localAuthenticatedUser = () => {
  const session = localSession();
  return {
    username: LOCAL_USER_ID,
    attributes: {
      sub: LOCAL_USER_ID,
      email: LOCAL_ID_TOKEN_PAYLOAD.email,
      'custom:company_id': LOCAL_COMPANY_ID,
      'custom:company_branch_id': LOCAL_COMPANY_BRANCH_ID,
    },
    getSignInUserSession: () => session,
    getUsername: () => LOCAL_USER_ID,
    signInUserSession: session,
  };
};

// Shape mirrors what getUserData() in contexts/Auth/Provider.jsx destructures off the monolith's
// /authenticated/ response. `apps` must contain lead_management_v2 AND showroom with hasAccess,
// or RouteAuthenticated.hasProductAccess() blocks every route.
export const localAuthentication = () => ({
  awsUsername: LOCAL_USER_ID,
  name: 'Local Dev',
  preferredName: 'Local',
  email: LOCAL_ID_TOKEN_PAYLOAD.email,
  mobilePrefix: '+60',
  mobile: '123456789',
  groups: LOCAL_GROUPS.map((code) => ({ code })),
  affiliate: {
    company: {
      service_company_id: LOCAL_COMPANY_ID,
      name: 'Local Dev Company',
      shortName: 'LOCAL',
      code: 'LOCAL',
      countryCode: 'MY',
      currencyCode: 'MYR',
      apps: [
        { appID: 'lead_management_v2', hasAccess: true, hasCallAccess: true },
        { appID: 'showroom', hasAccess: true },
        { appID: 'commission_tracker', hasAccess: false },
        { appID: 'loan_tracker', hasAccess: false },
        { appID: 'credit_control', hasAccess: false },
        { appID: 'analytics', hasAccess: false },
      ],
    },
    companyBranch: {
      service_company_branch_id: LOCAL_COMPANY_BRANCH_ID,
      name: 'Local Dev Branch',
      shortName: 'LOCALBR',
      isExternal: false,
    },
  },
});

// Fixtures for the two user-AppSync operations the bootstrap issues. Field sets match
// src/graphql/Users/fragments.js (UserFields / CompanyBranchFields) exactly — Apollo drops
// unrequested fields but throws on missing ones.
const localUsers = () => ([{
  id: LOCAL_USER_ID,
  company_id: LOCAL_COMPANY_ID,
  company_branch_id: LOCAL_COMPANY_BRANCH_ID,
  name: 'Local Dev',
  preferred_name: 'Local',
  email: LOCAL_ID_TOKEN_PAYLOAD.email,
  phone_number: '+60123456789',
  email_verified: true,
  phone_number_verified: true,
  created_at: '2026-01-01T00:00:00Z',
  last_modified: '2026-01-01T00:00:00Z',
  user_status: 'active',
  sms_mfa_status: 'disabled',
  groups: LOCAL_GROUPS,
  __typename: 'User',
}]);

const localCompanyBranches = () => ([{
  id: LOCAL_COMPANY_BRANCH_ID,
  name: 'Local Dev Branch',
  is_external: 'false',
  __typename: 'CompanyBranch',
}]);

// Resolves the user-AppSync operations locally so clientUser never leaves the machine. Anything
// else routed at clientUser resolves to an empty payload rather than hitting the network, which
// keeps "fully local" honest — a silent fall-through to real AWS is the failure mode this shim
// exists to prevent.
export const localUserResolve = (operationName) => {
  switch (operationName) {
    case 'listUsers':
      return { listUsers: { users: localUsers(), count: 1, __typename: 'ListUserResponse' } };
    case 'listCompanyBranches':
      return {
        listCompanyBranches: {
          company_branches: localCompanyBranches(),
          count: 1,
          __typename: 'ListCompanyBranchesResponse',
        },
      };
    case 'listUserGroups':
      return { listUserGroups: { user_groups: [], count: 0, __typename: 'ListUserGroupsResponse' } };
    case 'listActivities':
      return { listActivities: { items: [], count: 0, __typename: 'ListActivitiesResponse' } };
    default:
      // eslint-disable-next-line no-console
      console.warn(`[authShim] unhandled user-API operation "${operationName}" -> empty result`);
      return {};
  }
};

// Patch the Amplify surface the app calls. Done once, before the Apollo clients are built in
// index.jsx, so every generateLink() authLink picks up the forged token via Auth.currentSession().
/* eslint-disable no-param-reassign */ // patching the Amplify singletons in place is the point
export const installAuthShim = (Auth, API) => {
  if (!LOCAL_AUTH_SHIM) return;

  Auth.currentSession = async () => localSession();
  Auth.currentAuthenticatedUser = async () => localAuthenticatedUser();
  Auth.currentUserInfo = async () => ({
    username: LOCAL_USER_ID,
    attributes: localAuthenticatedUser().attributes,
  });
  Auth.signOut = async () => undefined;

  // setCurrentSession() POSTs /usersession/create to mint the user_sessions row. Locally that row
  // is already seeded by each stack's local/seed.sh, so this only has to succeed, not do work.
  const realPost = API.post ? API.post.bind(API) : null;
  API.post = async (apiName, path, init) => {
    if (apiName === 'USER') {
      return { id: LOCAL_SESSION_ID, session_token_id: LOCAL_SESSION_ID, user_id: LOCAL_USER_ID };
    }
    if (!realPost) throw new Error(`[authShim] no API.post available for ${apiName}${path}`);
    return realPost(apiName, path, init);
  };

  // eslint-disable-next-line no-console
  console.warn(
    `[authShim] LOCAL DEV: auth is stubbed (user ${LOCAL_USER_ID}, company ${LOCAL_COMPANY_ID}). `
    + 'Never enable REACT_APP_LOCAL_AUTH_SHIM outside local development.',
  );
};
